import React, { useEffect, useState } from 'react';
import type { DriveItem } from '../types';
import Icon from '../components/Icon';
import Button from '../components/Button';
import { Thumbnail } from '../components/TypeGlyph';
import {
  getContentUrl, getDownloadUrl, getPreviewTarget, fetchTextContent,
  type PreviewTarget, type PreviewUnavailableReason,
} from '../services/api';
import { getOfflineBlobUrl } from '../services/offlineCache';

interface Props {
  item: DriveItem;
  onClose: () => void;
}

// Extensions we render as plain text even when the server's mime_category isn't
// 'text' — uploads often arrive as application/octet-stream from the browser.
const TEXT_EXTS = new Set([
  'txt', 'text', 'log', 'md', 'markdown', 'csv', 'tsv', 'json', 'xml', 'yml', 'yaml',
  'ini', 'conf', 'cfg', 'env', 'sql', 'sh', 'bash', 'zsh', 'js', 'jsx', 'ts', 'tsx',
  'css', 'scss', 'html', 'htm', 'py', 'rb', 'go', 'rs', 'java', 'c', 'h', 'cpp', 'php', 'svg',
]);

// Documents worth attempting a PDF preview for. PDFs serve directly; the rest
// depend on the server-side render, which may not have happened yet.
const DOC_EXTS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'odt', 'ods', 'odp', 'rtf',
]);

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

const REASON_COPY: Record<PreviewUnavailableReason, { title: string; body: string; retry: boolean }> = {
  scanning: {
    title: 'Still being checked',
    body: 'This file is going through a virus scan. Previews unlock as soon as it passes.',
    retry: true,
  },
  pending: {
    title: 'Preview is being prepared',
    body: 'We\'re converting this document so it can be read here. This usually takes a few seconds.',
    retry: true,
  },
  failed: {
    title: 'Preview could not be generated',
    body: 'Something went wrong converting this document. You can still download it.',
    retry: true,
  },
  unavailable: {
    title: 'Preview not available on this server',
    body: 'Document conversion isn\'t enabled in this environment, so Office files can only be downloaded.',
    retry: false,
  },
  unsupported: {
    title: 'No preview available',
    body: 'This file type can\'t be displayed in the browser.',
    retry: false,
  },
};

export default function Viewer({ item, onClose }: Props) {
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [text, setText] = useState<{ text: string; truncated: boolean } | null>(null);
  const [unavailable, setUnavailable] = useState<PreviewUnavailableReason | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const ext = extOf(item.name);
  const mime = item.mime_category ?? 'application';
  const isMedia = mime === 'image' || mime === 'video' || mime === 'audio';
  const isText = !isMedia && (mime === 'text' || TEXT_EXTS.has(ext));
  const isDoc = !isMedia && !isText && DOC_EXTS.has(ext);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setContentUrl(null);
    setPreview(null);
    setText(null);
    setUnavailable(null);

    async function load() {
      try {
        if (isText) {
          setText(await fetchTextContent(item.id));
          return;
        }
        if (isDoc) {
          setPreview(await getPreviewTarget(item.id));
          return;
        }
        // Media: prefer the offline-cached copy when this item was marked
        // "Available offline" — works with no network at all.
        const offline = await getOfflineBlobUrl(item.id);
        setContentUrl(offline ?? await getContentUrl(item.id));
      } catch (err) {
        if (cancelled) return;
        const reason = (err as { reason?: PreviewUnavailableReason }).reason;
        setUnavailable(reason ?? 'unsupported');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [item.id, isText, isDoc, attempt]);

  const handleDownload = async () => {
    try {
      const url = await getDownloadUrl(item.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {}
  };

  return (
    <div className="t-viewer" onClick={onClose}>
      {/* Top bar */}
      <div className="t-viewer-bar" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <Thumbnail item={item} width={28} height={28} />
          <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
          {preview?.kind === 'derived' && (
            <span style={{ flexShrink: 0, fontSize: 11, color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999, padding: '1px 7px' }}>
              converted view
            </span>
          )}
        </div>
        {preview && (
          <Button
            kind="ghost"
            size="sm"
            icon="external"
            style={{ color: 'rgba(255,255,255,0.7)' }}
            onClick={() => window.open(preview.url, '_blank', 'noopener')}
          >
            Open in new tab
          </Button>
        )}
        <Button kind="ghost" size="sm" icon="download" style={{ color: 'rgba(255,255,255,0.7)' }} onClick={handleDownload}>Download</Button>
        <Button kind="ghost" size="sm" icon="x" iconOnly style={{ color: 'rgba(255,255,255,0.7)' }} onClick={onClose} />
      </div>

      {/* Body */}
      <div className="t-viewer-body" onClick={e => e.stopPropagation()}>
        {loading ? (
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="t-spinner" style={{ width: 16, height: 16 }} />
            {isDoc ? 'Preparing preview…' : 'Loading…'}
          </div>
        ) : unavailable ? (
          <Unavailable
            reason={unavailable}
            onDownload={handleDownload}
            onRetry={REASON_COPY[unavailable].retry ? () => setAttempt(a => a + 1) : undefined}
          />
        ) : preview ? (
          // Rendered by the browser's built-in PDF viewer — pagination, zoom,
          // text selection and in-document search all come for free.
          // alignSelf stretch rather than height:100% — .t-viewer-body centers
          // its children, so a percentage height would overflow its padding.
          <iframe
            src={preview.url}
            title={item.name}
            style={{ width: '100%', maxWidth: 1100, alignSelf: 'stretch', border: 'none', borderRadius: 8, background: '#fff' }}
          />
        ) : text ? (
          <div style={{ width: '100%', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 0 }}>
            <pre
              style={{
                width: '100%', maxWidth: 900, flex: 1, overflow: 'auto', margin: 0,
                padding: '18px 20px', background: '#fff', color: '#1a1a2e', borderRadius: 8,
                fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.6,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}
            >
              {text.text}
            </pre>
            {text.truncated && (
              <div style={{ flexShrink: 0, marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                Showing the first 2 MB — download the file to read all of it.
              </div>
            )}
          </div>
        ) : mime === 'image' && contentUrl ? (
          <img src={contentUrl} alt={item.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
        ) : mime === 'video' && contentUrl ? (
          <video src={contentUrl} controls style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        ) : mime === 'audio' && contentUrl ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <div style={{ marginBottom: 16 }}><Icon name="music" size={48} color="rgba(255,255,255,0.4)" /></div>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>{item.name}</div>
            <audio src={contentUrl} controls style={{ width: 320 }} />
          </div>
        ) : (
          <Unavailable reason="unsupported" onDownload={handleDownload} />
        )}
      </div>
    </div>
  );
}

function Unavailable({ reason, onDownload, onRetry }: {
  reason: PreviewUnavailableReason;
  onDownload: () => void;
  onRetry?: () => void;
}) {
  const copy = REASON_COPY[reason];
  return (
    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', maxWidth: 400 }}>
      <Icon name={reason === 'unsupported' ? 'file' : 'clock'} size={52} color="rgba(255,255,255,0.25)" />
      <div style={{ marginTop: 16, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>{copy.title}</div>
      <div style={{ marginTop: 8, fontSize: 'var(--fs-sm)', marginBottom: 22, lineHeight: 1.6 }}>{copy.body}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        {onRetry && (
          <Button kind="outline" icon="refresh" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }} onClick={onRetry}>
            Check again
          </Button>
        )}
        <Button kind="outline" icon="download" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }} onClick={onDownload}>
          Download
        </Button>
      </div>
    </div>
  );
}
